'use client';

import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Check, Lock, Pencil, X } from 'lucide-react';
import type { SearchUiContent } from '@/lib/ui-content/search-ui-content';
import { ButtonSuggestion } from './chat/message';
import { UserTextWithLinks } from './chat/user-text-with-links';
import {
  HISTORY_COLLAPSE_MS,
  HISTORY_FADE_MS,
  HISTORY_RESTORE_MS,
  useAboveBarReveal,
} from './above-bar-prompt-panel';

export interface PastAnswerTurn {
  kind: 'answer';
  /** Id of the assistant message that asked this question; targets the edit and keys the row. */
  assistantMessageId: string;
  questionText: string;
  suggestedAnswers: string[];
  /** The user's reply, already unwrapped from the "[User's answer …]:" convention. */
  answerText: string;
  /** Links the reply carried: their placeholders in `answerText` are drawn as pills. */
  answerLinks?: LinkRef[];
}

export interface OriginalQueryTurn {
  kind: 'query';
  queryText: string;
  /** Links the query carried: their placeholders in `queryText` are drawn as pills. */
  queryLinks?: LinkRef[];
}

type LinkRef = { url: string; host: string };

export type HistoryTurn = OriginalQueryTurn | PastAnswerTurn;

interface AboveBarHistoryProps {
  turns: HistoryTurn[];
  searchContent: SearchUiContent;
  /** A turn is streaming — editing is disabled, same gate as the live answer chips. */
  isLoading: boolean;
  /**
   * Confirm an edit. `afterAssistantId === null` means the original query was
   * edited (conversation restarts from scratch); otherwise the conversation is
   * truncated to just after that assistant question and the new answer replayed.
   */
  onConfirmEdit: (args: { afterAssistantId: string | null; newAnswerText: string }) => void;
  /** True while any editor is open, so the panel can suspend its idle auto-collapse. */
  onEditingActiveChange: (active: boolean) => void;
}

const questionTextClass =
  'm-auto max-w-[80%] text-center max-sm:leading-tight text-lg text-gray-600 dark:text-white';

/**
 * The scrollback history above the live question: the original query and every
 * answered turn, each locked behind a separate Edit control. The answer pill
 * itself is deliberately inert — a stray tap while scrolling must never open an
 * editor or change anything, so tapping it only plays a small nudge.
 */
export function AboveBarHistory({
  turns,
  searchContent,
  isLoading,
  onConfirmEdit,
  onEditingActiveChange,
}: AboveBarHistoryProps) {
  const phase = useAboveBarReveal();
  // 'query' | assistantMessageId | null
  const [editingId, setEditingId] = useState<string | null>(null);
  // Set at confirm time: rows AFTER this index dim out while the regenerate lands.
  const [staleAfterIndex, setStaleAfterIndex] = useState<number | null>(null);
  const submittedRef = useRef(false);

  // "Editing active" doubles as "hold the panel open": both an open editor and
  // an in-flight stale cascade must suspend the idle collapse, or the panel
  // hides the history mid-edit / mid-animation.
  useEffect(() => {
    onEditingActiveChange(editingId !== null || staleAfterIndex !== null);
  }, [editingId, staleAfterIndex, onEditingActiveChange]);

  // The regenerate truncates `messages`, which shrinks `turns` — once the stale
  // tail is really gone from the data, drop the visual stale marker and re-allow edits.
  useEffect(() => {
    if (staleAfterIndex !== null && turns.length <= staleAfterIndex + 1) {
      setStaleAfterIndex(null);
      submittedRef.current = false;
    }
  }, [turns.length, staleAfterIndex]);

  // While a turn streams, no editor should stay open (its Edit affordances are hidden too).
  useEffect(() => {
    if (isLoading && editingId !== null) setEditingId(null);
  }, [isLoading, editingId]);

  const confirmEdit = (index: number, afterAssistantId: string | null, newAnswerText: string) => {
    if (submittedRef.current) return;
    const trimmed = newAnswerText.trim();
    if (!trimmed) return;
    submittedRef.current = true;
    setEditingId(null);
    setStaleAfterIndex(index);
    onConfirmEdit({ afterAssistantId, newAnswerText: trimmed });
  };

  const editLabel = searchContent.editAnswer ?? 'Edit';
  const cancelLabel = searchContent.cancelEdit ?? 'Cancel';

  // Hidden at rest — the panel reveals on a scroll-up gesture and drains away
  // again after idle (see AboveBarRevealPhase). A live cascade keeps it mounted
  // so the stale dim-out isn't cut short.
  if (phase === 'hidden' && staleAfterIndex === null) return null;

  const rows = turns.map((turn, index) => {
    const rowKey = turn.kind === 'query' ? 'query' : turn.assistantMessageId;
    const isEditing = editingId === rowKey;
    const isStale = staleAfterIndex !== null && index > staleAfterIndex;

    return (
      <div
        key={rowKey}
        className={`flex w-full flex-col items-center gap-2 border-b border-slate-200/60 pb-4 mb-4 dark:border-slate-700/40 transition-all duration-300 ${
          isStale ? 'opacity-30 grayscale pointer-events-none' : ''
        }`}
      >
        {turn.kind === 'query' ? (
          <QueryRow
            turn={turn}
            isEditing={isEditing}
            canEdit={!isLoading && staleAfterIndex === null}
            editLabel={editLabel}
            editAriaLabel={`${editLabel}: ${turn.queryText}`}
            cancelLabel={cancelLabel}
            confirmLabel={searchContent.confirmEdit ?? 'Confirm edit'}
            onStartEdit={() => setEditingId('query')}
            onCancel={() => setEditingId(null)}
            onConfirm={(text) => confirmEdit(index, null, text)}
          />
        ) : (
          <AnswerRow
            turn={turn}
            isEditing={isEditing}
            canEdit={!isLoading && staleAfterIndex === null}
            editLabel={editLabel}
            editAriaLabel={`${editLabel}: ${turn.questionText}`}
            cancelLabel={cancelLabel}
            onStartEdit={() => setEditingId(turn.assistantMessageId)}
            onCancel={() => setEditingId(null)}
            onConfirm={(choice) => confirmEdit(index, turn.assistantMessageId, choice)}
          />
        )}
      </div>
    );
  });

  // The deplete, and catching it. Opacity drains over HISTORY_FADE_MS with the
  // height left alone, so nothing shifts and the reader's scroll position stays
  // theirs; only once the block is invisible does its height melt away, over
  // HISTORY_COLLAPSE_MS, which is what keeps the panel from snapping shut at the
  // end. Any wheel, touch, tap or focus flips both back at HISTORY_RESTORE_MS,
  // and CSS interpolates from the CURRENT computed values — so catching it
  // half-drained resumes from half-drained instead of restarting the animation.
  const dimmed = phase === 'fading' || phase === 'collapsing';
  const collapsed = phase === 'collapsing';

  return (
    <div
      className="grid w-full motion-reduce:transition-none"
      style={{
        gridTemplateRows: collapsed ? '0fr' : '1fr',
        opacity: dimmed ? 0 : 1,
        transitionProperty: 'grid-template-rows, opacity',
        transitionDuration: `${collapsed ? HISTORY_COLLAPSE_MS : HISTORY_RESTORE_MS}ms, ${
          dimmed ? HISTORY_FADE_MS : HISTORY_RESTORE_MS
        }ms`,
        transitionTimingFunction: 'ease-out, linear',
      }}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="flex w-full flex-col">{rows}</div>
      </div>
    </div>
  );
}

function QueryRow({
  turn,
  isEditing,
  canEdit,
  editLabel,
  editAriaLabel,
  cancelLabel,
  confirmLabel,
  onStartEdit,
  onCancel,
  onConfirm,
}: {
  turn: OriginalQueryTurn;
  isEditing: boolean;
  canEdit: boolean;
  editLabel: string;
  editAriaLabel: string;
  cancelLabel: string;
  confirmLabel: string;
  onStartEdit: () => void;
  onCancel: () => void;
  onConfirm: (text: string) => void;
}) {
  const [draft, setDraft] = useState(turn.queryText);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      setDraft(turn.queryText);
      requestAnimationFrame(() => {
        // preventScroll: focusing an element inside a scroller natively scrolls it
        // into view, which is a second way an upper row's editor yanked the panel.
        // The row is already on screen — its own Edit button was just clicked.
        inputRef.current?.focus({ preventScroll: true });
        inputRef.current?.select();
      });
    }
  }, [isEditing, turn.queryText]);

  if (isEditing) {
    return (
      <div className="flex w-full max-w-md items-center gap-2 px-2">
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') onConfirm(draft);
            if (e.key === 'Escape') onCancel();
          }}
          className="min-w-0 flex-1 rounded-xl border border-slate-200/80 bg-white/60 px-3.5 py-2 text-sm text-gray-700 backdrop-blur-sm focus:border-cornflowerblue/60 focus:outline-none dark:border-slate-700/60 dark:bg-[rgb(11,14,48)]/50 dark:text-gray-200"
        />
        <button
          type="button"
          aria-label={confirmLabel}
          onClick={() => onConfirm(draft)}
          className="grid h-8 w-8 flex-none place-items-center rounded-full bg-cornflowerblue text-white transition-opacity hover:opacity-90"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          type="button"
          aria-label={cancelLabel}
          onClick={onCancel}
          className="grid h-8 w-8 flex-none place-items-center rounded-full bg-slate-200/80 text-gray-600 transition-colors hover:bg-slate-300/80 dark:bg-slate-700/60 dark:text-gray-300 dark:hover:bg-slate-600/60"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <>
      <p className={questionTextClass}>
        &ldquo;<UserTextWithLinks text={turn.queryText} links={turn.queryLinks ?? []} interactive={false} />&rdquo;
      </p>
      {canEdit && <EditAffordance label={editLabel} ariaLabel={editAriaLabel} onClick={onStartEdit} />}
    </>
  );
}

function AnswerRow({
  turn,
  isEditing,
  canEdit,
  editLabel,
  editAriaLabel,
  cancelLabel,
  onStartEdit,
  onCancel,
  onConfirm,
}: {
  turn: PastAnswerTurn;
  isEditing: boolean;
  canEdit: boolean;
  editLabel: string;
  editAriaLabel: string;
  cancelLabel: string;
  onStartEdit: () => void;
  onCancel: () => void;
  onConfirm: (choice: string) => void;
}) {
  if (isEditing) {
    return (
      <>
        <p className={questionTextClass}>{turn.questionText}</p>
        <div className="mt-1 flex flex-wrap justify-center gap-2 max-sm:gap-1.5">
          {turn.suggestedAnswers.map((choice) => (
            <ButtonSuggestion
              key={choice}
              choice={choice}
              handleSuggestionClick={(picked) => {
                // Re-picking the same answer is a no-op close, not a regenerate.
                if (picked === turn.answerText) onCancel();
                else onConfirm(picked);
              }}
            />
          ))}
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl px-3 py-2 text-sm font-medium text-gray-500 transition-colors hover:bg-slate-100/70 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200"
          >
            {cancelLabel}
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <p className={questionTextClass}>{turn.questionText}</p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <motion.span
          whileTap={{ scale: 0.96 }}
          className="inline-flex cursor-default select-none items-center gap-1.5 rounded-xl border border-slate-200/70 bg-white/40 px-3.5 py-2 text-sm font-medium text-gray-500 backdrop-blur-sm dark:border-slate-700/50 dark:bg-[rgb(11,14,48)]/35 dark:text-gray-400"
        >
          <Lock className="h-3 w-3 opacity-60" aria-hidden />
          <span>
            <UserTextWithLinks text={turn.answerText} links={turn.answerLinks ?? []} interactive={false} />
          </span>
        </motion.span>
        {canEdit && <EditAffordance label={editLabel} ariaLabel={editAriaLabel} onClick={onStartEdit} />}
      </div>
    </>
  );
}

function EditAffordance({ label, ariaLabel, onClick }: { label: string; ariaLabel: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-semibold tracking-wide text-gray-400 opacity-75 transition-all hover:bg-cornflowerblue/10 hover:text-cornflowerblue hover:opacity-100 dark:text-gray-500 dark:hover:bg-cornflowerblue/15 dark:hover:text-cornflowerblue"
    >
      <Pencil className="h-3 w-3" aria-hidden />
      {label}
    </button>
  );
}
